import { useRouteInfo } from "../../core/routing/routingHooks";
import Login from "../../login/website/Login";
import React, { memo, ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

declare global {
  interface Window {
    // Rendered into the guest site template by the backend as "True"/"False".
    __GUEST_AUTHENTICATED__?: string;
  }
}

type LoginWrapperProps = {
  children: ReactNode;
};

/**
 * Swaps the site for the login page and redirects between the two based on
 * the guest's authentication state.
 */
const LoginWrapper = ({ children }: LoginWrapperProps) => {
  const route = useRouteInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const guestAuthenticated = window.__GUEST_AUTHENTICATED__ === "True";

  useEffect(() => {
    // If user is not authenticated and not already on login page, redirect to login
    if (!guestAuthenticated && route.name !== "login") {
      const currentPath = `${location.pathname}${location.search}`;
      navigate(`/login?next=${encodeURIComponent(currentPath)}`, {
        replace: true,
      });
    }

    // If user is authenticated and on login page, redirect to next or home
    if (guestAuthenticated && route.name === "login") {
      const searchParams = new URLSearchParams(location.search);
      // searchParams.get() already decodes the value. Only follow same-site
      // paths: reject absolute URLs, protocol-relative `//host`, and `/\host`.
      const next = searchParams.get("next");
      const isSafeNext =
        next &&
        next.startsWith("/") &&
        !next.startsWith("//") &&
        !next.startsWith("/\\");
      navigate(isSafeNext ? next : "/", { replace: true });
    }
  }, [
    guestAuthenticated,
    route.name,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Unauthenticated guests only get the login page. The backend omits site
  // content (pages, siteDetails, etc.) for them, so rendering the site while
  // the redirect to /login is pending would crash on the missing data.
  return !guestAuthenticated || route.name === "login" ? (
    <Login />
  ) : (
    <>{children}</>
  );
};

const MemoizedLoginWrapper = memo(LoginWrapper);

export default MemoizedLoginWrapper;
